import { useMemo } from "react";
import { Control, FieldValues, Path, useWatch } from "react-hook-form";

import { serializeNodeConfigContext } from "@/utils/nodeConfigContext";
import { isRecord } from "@/utils/isRecord";

function getPathSegments(fieldName: string): string[] {
  return fieldName.split(".").filter((segment) => segment !== "");
}

function getConfigValues(
  values: unknown,
  fieldName: string,
  watchName: string | undefined
): Record<string, unknown> | null {
  if (!isRecord(values)) return null;
  if (watchName !== undefined) return values;
  return values;
}

function getExcludeKey(fieldName: string): string {
  const pathSegments = getPathSegments(fieldName);
  return pathSegments[pathSegments.length - 1] ?? fieldName;
}

function getWatchName<TFieldValues extends FieldValues>(
  fieldName: string
): Path<TFieldValues> | undefined {
  const pathSegments = getPathSegments(fieldName);
  const [nodeKey] = pathSegments;
  return pathSegments.length > 1 && nodeKey
    ? (nodeKey as Path<TFieldValues>)
    : undefined;
}

export function appendNodeConfigContext(
  context: string | undefined,
  nodeConfigContext: string | null
): string | undefined {
  if (nodeConfigContext === null) return context;
  if (!context) return nodeConfigContext;
  return `${context}\n\n${nodeConfigContext}`;
}

export function useNodeConfigContext<TFieldValues extends FieldValues>(
  control: Control<TFieldValues>,
  fieldName: string
): string | null {
  const watchName = getWatchName<TFieldValues>(fieldName);
  const values = useWatch({
    control,
    name: watchName as Path<TFieldValues>,
  });

  return useMemo(() => {
    const configValues = getConfigValues(values, fieldName, watchName);
    if (configValues === null) return null;
    return serializeNodeConfigContext(configValues, {
      excludeKeys: [getExcludeKey(fieldName)],
    });
  }, [fieldName, values, watchName]);
}
